// /home/szymon/kreator2/frontend/src/components/editor/useEditorCanvasLogic.js v5.0
import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { buildSnapGuides } from "./snapUtils";
import { RULER_SIZE } from "./Rulers";
import { parseGuideExpression } from "./GuideUtils";

const EDGE_THRESHOLD = 5;              // Progi przyciągania
const CENTER_THRESHOLD = 10;
const PERSONALIZATION_THRESHOLD = 100;

export function useEditorCanvasLogic({
  pages,
  currentPage,
  zoomFactor,
  selectedShapeId,
  setSelectedShapeId,
  editingTextId,
  setEditingTextId,
  handleChangeText,
  handleChangeImage,
  handleChangeSvg,
  forceRefresh,
  publicMode,
  guides,
  onCanvasInsideChange,
  onCanvasPosChange,
  stageRef,
  fakeDraggingRebus,
  setFakeDraggingRebus,
  onRebusDropped,
  isProjectPersonalized // Dodajemy nową flagę
}) {
  const layerRef = useRef(null);
  const transformerRef = useRef(null);

  const [insideCanvas, setInsideCanvas] = useState(false);
  const lastPointerPosRef = useRef({ x: 0, y: 0 });
  const [tempGuides, setTempGuides] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const validPage = useMemo(() => {
    return pages && currentPage >= 0 && currentPage < pages.length;
  }, [pages, currentPage]);

  // Bazowe wymiary strony
  const { baseWidth, baseHeight } = useMemo(() => {
    if (!validPage) {
      return { baseWidth: 800, baseHeight: 600 };
    }
    const pg = pages[currentPage];
    return {
      baseWidth: pg.baseWidth || 800,
      baseHeight: pg.baseHeight || 600
    };
  }, [validPage, pages, currentPage]);

  // Obliczamy wymiary sceny
  const {
    stageWidth,
    stageHeight,
    stageOffsetX,
    stageOffsetY,
    containerWidth,
    containerHeight
  } = useMemo(() => {
    const sWidth = Math.round(baseWidth * zoomFactor);
    const sHeight = Math.round(baseHeight * zoomFactor);
    const sOffsetX = RULER_SIZE + 10;
    const sOffsetY = RULER_SIZE + 10;
    const cWidth = sWidth + sOffsetX;
    const cHeight = sHeight + sOffsetY;
    return {
      stageWidth: sWidth,
      stageHeight: sHeight,
      stageOffsetX: sOffsetX,
      stageOffsetY: sOffsetY,
      containerWidth: cWidth,
      containerHeight: cHeight
    };
  }, [baseWidth, baseHeight, zoomFactor]);

  // Generujemy listę snapGuides
  const snapGuidesData = useMemo(() => {
    if (!guides || guides.length === 0) {
      //console.log("[DRAG DEBUG] snapGuidesData => brak guides");
      return [];
    }
    //console.log("[DRAG DEBUG] buildSnapGuides =>", guides);
    const built = buildSnapGuides(guides, baseWidth, baseHeight, parseGuideExpression);
    //console.log("[DRAG DEBUG] buildSnapGuides => wynik =", built);
    return built;
  }, [guides, baseWidth, baseHeight]);

  // findObjById
  const findObjById = useCallback(
    (shapeId) => {
      if (!validPage || !shapeId) return null;
      const pg = pages[currentPage];
      if (!pg) return null;
      const search = (arr) => arr?.find((item) => item.id === shapeId);

      let found = search(pg.texts);
      if (found) return { ...found, _type: "text" };

      found = search(pg.images);
      if (found) return { ...found, _type: "image" };

      found = search(pg.svgs);
      if (found) return { ...found, _type: "svg" };

      return null;
    },
    [validPage, pages, currentPage]
  );

  // Efekt: Transformera
  useEffect(() => {
    if (!validPage || !stageRef.current || !transformerRef.current || !layerRef.current) {
      return;
    }
    const stage = stageRef.current;
    const tr = transformerRef.current;
    const layer = layerRef.current;

    if (selectedShapeId) {
      const obj = findObjById(selectedShapeId);
      if (obj && !(publicMode && obj.flags?.locked)) {
        const selectedNode = stage.findOne(`#${selectedShapeId}`);
        if (selectedNode) {
          tr.nodes([selectedNode]);
          tr.keepRatio(obj._type === "image" || obj._type === "svg");
        } else {
          tr.nodes([]);
        }
      } else {
        tr.nodes([]);
      }
    } else {
      tr.nodes([]);
    }
    if (layer) layer.batchDraw();
  }, [
    selectedShapeId,
    findObjById,
    publicMode,
    validPage,
    stageRef,
    transformerRef,
    layerRef
  ]);

  // forceRefresh => batchDraw
  useEffect(() => {
    if (forceRefresh && layerRef.current) {
      layerRef.current.batchDraw();
    }
  }, [forceRefresh]);

  // ------------------ DRAG MOVE ------------------
  const handleObjectDragMove = useCallback(
    (dragEventData) => {
      //console.log("[DRAG DEBUG] handleObjectDragMove =>", dragEventData);
      setIsDragging(true);

      if (!dragEventData?.target || typeof dragEventData.target.id !== "function") {
        //console.log("[DRAG DEBUG] brak target lub target.id() => koniec");
        return;
      }
      const target = dragEventData.target;
      const shapeId = target.id();
      const obj = findObjById(shapeId);
      //console.log("[DRAG DEBUG] shapeId =", shapeId, "found obj=", obj);

      if (!obj) {
        //console.log("[DRAG DEBUG] obiekt nie znaleziony => koniec");
        return;
      }
      if (publicMode && obj.flags?.locked) {
        //console.log("[DRAG DEBUG] obiekt jest locked => stopDrag");
        target.stopDrag();
        return;
      }

      // Aktualna pozycja, wymiary
      const unscaledX = target.x();
      const unscaledY = target.y();
      const scaleX = target.scaleX() || 1;
      const scaleY = target.scaleY() || 1;
      
      // Priorytet dla wymiarów z obj (aktualizowanych przez EditableText), fallback na getClientRect
      const baseW = obj.width || target.width(); // Użyj obj.width jeśli dostępne
      const baseH = obj.height || target.height(); // Użyj obj.height jeśli dostępne
      const currentW = baseW * scaleX;
      const currentH = baseH * scaleY;

      //console.log("[DRAG DEBUG] unscaledX=", unscaledX, "unscaledY=", unscaledY);
      //console.log("[DRAG DEBUG] Using dimensions: baseW=", baseW, "baseH=", baseH, " (from obj:", !!obj.width, ")");
      //console.log("[DRAG DEBUG] currentW=", currentW, "currentH=", currentH);

      let finalX = unscaledX;
      let finalY = unscaledY;
      const activeGuidesToShow = [];
      const potentialSnaps = [];

      // Iteracja po snapGuidesData
      snapGuidesData.forEach((guide, idx) => {
        // Domyślnie 5 px
        let threshold = EDGE_THRESHOLD;

        if (guide.snap === "center") {
          if (
            obj._type === "text" &&
            (obj.flags?.personalize || obj.flags?.personalization)
          ) {
            threshold = PERSONALIZATION_THRESHOLD; // 100
          } else {
            threshold = CENTER_THRESHOLD; // 10
          }
        }

        const nodeRect = {
          x: unscaledX,
          y: unscaledY,
          width: currentW,
          height: currentH
        };
        let diff = Infinity;
        let snapPos = null;

        //console.log(
        //  `[DRAG DEBUG] guide #${idx}: orientation=${guide.orientation}, pos=${guide.pos}, snap=${guide.snap}, threshold=${threshold}`
       // );

        // orientation: "V" => oś X
        if (guide.orientation === "V") {
          if (guide.snap === "start") {
            diff = Math.abs(nodeRect.x - guide.pos);
            if (diff < threshold) snapPos = guide.pos;
          } else if (guide.snap === "center") {
            const nodeCenterX = nodeRect.x + nodeRect.width / 2;
            diff = Math.abs(nodeCenterX - guide.pos);
            if (diff < threshold) snapPos = guide.pos - nodeRect.width / 2;
          } else if (guide.snap === "end") {
            const nodeEndX = nodeRect.x + nodeRect.width;
            diff = Math.abs(nodeEndX - guide.pos);
            if (diff < threshold) snapPos = guide.pos - nodeRect.width;
          }
          if (snapPos !== null) {
            potentialSnaps.push({
              axis: "x",
              finalPos: snapPos,
              diff,
              guide
            });
          }
        } else {
          // orientation: "H" => oś Y
          if (guide.snap === "start") {
            diff = Math.abs(nodeRect.y - guide.pos);
            if (diff < threshold) snapPos = guide.pos;
          } else if (guide.snap === "center") {
            const nodeCenterY = nodeRect.y + nodeRect.height / 2;
            diff = Math.abs(nodeCenterY - guide.pos);
            if (diff < threshold) snapPos = guide.pos - nodeRect.height / 2;
          } else if (guide.snap === "end") {
            const nodeEndY = nodeRect.y + nodeRect.height;
            diff = Math.abs(nodeEndY - guide.pos);
            if (diff < threshold) snapPos = guide.pos - nodeRect.height;
          }
          if (snapPos !== null) {
            potentialSnaps.push({
              axis: "y",
              finalPos: snapPos,
              diff,
              guide
            });
          }
        }
      });

      //console.log("[DRAG DEBUG] potentialSnaps =>", potentialSnaps);

      const bestXSnap = potentialSnaps
        .filter((s) => s.axis === "x")
        .sort((a, b) => a.diff - b.diff)[0];
      const bestYSnap = potentialSnaps
        .filter((s) => s.axis === "y")
        .sort((a, b) => a.diff - b.diff)[0];

      //console.log("[DRAG DEBUG] bestXSnap =>", bestXSnap);
      //console.log("[DRAG DEBUG] bestYSnap =>", bestYSnap);

      if (bestXSnap) {
        finalX = bestXSnap.finalPos;
        // Dodajmy prowadnicę do rysowania
        activeGuidesToShow.push({
          points: [bestXSnap.guide.pos, 0, bestXSnap.guide.pos, baseHeight],
          orientation: "V"
        });
      }
      if (bestYSnap) {
        finalY = bestYSnap.finalPos;
        activeGuidesToShow.push({
          points: [0, bestYSnap.guide.pos, baseWidth, bestYSnap.guide.pos],
          orientation: "H"
        });
      }

      //console.log("[DRAG DEBUG] finalX=", finalX, " finalY=", finalY);

      // Ustawiamy nową pozycję
      // Dodano sprawdzenie isNaN, aby uniknąć błędów, jeśli obliczenia zawiodą
      if (typeof finalX === 'number' && !isNaN(finalX) && typeof finalY === 'number' && !isNaN(finalY)) {
        target.position({ x: finalX, y: finalY });
      } else {
         console.error("[DRAG DEBUG] Attempted to set invalid position:", { finalX, finalY });
      }
      setTempGuides(activeGuidesToShow);

      if (layerRef.current) {
        layerRef.current.batchDraw();
      }

    },
    [
      findObjById,
      publicMode,
      snapGuidesData,
      baseWidth,
      baseHeight,
      setTempGuides,
      layerRef,
      isProjectPersonalized // Dodajemy zależność
    ]
  );

  // DRAG END
  const handleDragEnd = useCallback(
    (dragEndEventData) => {
      console.warn("[DRAG END] handleDragEnd wywołane");
      setIsDragging(false);
      setTempGuides([]);
      if (layerRef.current) {
        layerRef.current.batchDraw();
      }

      const target = dragEndEventData.target;
      const shapeId = target.id();
      const obj = findObjById(shapeId);
      if (!obj) {
        console.warn("[DRAG END] handleDragEnd => obiekt nie znaleziony", shapeId);
        return;
      }

      if (publicMode && obj.flags?.locked) {
        console.warn("[DRAG END] handleDragEnd => obiekt locked => koniec");
        return;
      }

      const finalPosition = target.position();
      // Usunięto logi debugowania z DRAG END

      const updateData = {
        id: shapeId,
        x: finalPosition.x,
        y: finalPosition.y
      };

      if (obj._type === "text") handleChangeText(updateData);
      else if (obj._type === "image") handleChangeImage(updateData);
      else if (obj._type === "svg") handleChangeSvg(updateData);
      
      // Upewniamy się, że flaga window.preventProjectReload jest ustawiona
      console.warn("[DRAG END] Ustawiam window.preventProjectReload = true");
      window.preventProjectReload = true;
    },
    [findObjById, publicMode, handleChangeText, handleChangeImage, handleChangeSvg]
  );

  // MOUSE DOWN STAGE
  const handleStageMouseDown = useCallback(
    (e) => {
      if (!validPage) return;
      const target = e.target;
      const stage = target.getStage();
      if (target === stage) {
        setSelectedShapeId(null);
        if (editingTextId) setEditingTextId(null);
        return;
      }
      if (target.getParent()?.getClassName?.() === "Transformer") {
        return;
      }

      const shapeId = target.id() || null;
      if (!shapeId) return;

      const obj = findObjById(shapeId);
      if (!obj) return;

      if (publicMode && obj.flags?.locked) {
        e.evt.stopPropagation();
        setSelectedShapeId(null);
        return;
      }

      if (editingTextId && shapeId !== editingTextId) {
        setEditingTextId(null);
      }
      setSelectedShapeId(shapeId);

      // Usunięto logi debugowania z MOUSE DOWN
    },
    [
      validPage,
      editingTextId,
      setEditingTextId,
      setSelectedShapeId,
      publicMode,
      findObjById
    ]
  );

  // CANVAS ENTER/LEAVE/MOVE
  const handleMouseEnterCanvas = useCallback(() => {
    setInsideCanvas(true);
    if (onCanvasInsideChange) onCanvasInsideChange(true);
  }, [onCanvasInsideChange]);

  const handleMouseLeaveCanvas = useCallback(() => {
    setInsideCanvas(false);
    if (onCanvasInsideChange) onCanvasInsideChange(false);
  }, [onCanvasInsideChange]);

  const handleMouseMoveCanvas = useCallback(
    (e) => {
      if (!e.currentTarget || typeof e.currentTarget.getStage !== "function") {
        return;
      }
      const stage = e.currentTarget.getStage();
      if (!stage) return;
      const pointerPos = stage.getPointerPosition();
      if (pointerPos) {
        const unscaledPos = {
          x: pointerPos.x / zoomFactor,
          y: pointerPos.y / zoomFactor
        };
        lastPointerPosRef.current = unscaledPos;
        if (onCanvasPosChange) onCanvasPosChange(unscaledPos);
      }
    },
    [zoomFactor, onCanvasPosChange]
  );

  // DRAG OVER / DROP
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      if (fakeDraggingRebus && fakeDraggingRebus.file) {
        console.log("[useEditorCanvasLogic] handleDrop => fake rebus drop");
        const stage = stageRef.current;
        if (!stage) return;

        const pointerPosition = stage.getPointerPosition();
        if (!pointerPosition) return;

        const dropX = pointerPosition.x / zoomFactor;
        const dropY = pointerPosition.y / zoomFactor;
        onRebusDropped?.({
          file: fakeDraggingRebus.file,
          width: fakeDraggingRebus.width,
          height: fakeDraggingRebus.height,
          x: dropX,
          y: dropY,
          isRebus: true
        });
        setFakeDraggingRebus(null);
        return;
      }
      console.log("[useEditorCanvasLogic] handleDrop => standard drop (unhandled).");
    },
    [fakeDraggingRebus, setFakeDraggingRebus, onRebusDropped, stageRef, zoomFactor]
  );

  const handleMouseUpCanvas = useCallback(() => {}, []);

  return {
    layerRef,
    transformerRef,
    insideCanvas,
    lastPointerPosRef,
    tempGuides,
    validPage,
    baseWidth,
    baseHeight,
    stageWidth,
    stageHeight,
    stageOffsetX,
    stageOffsetY,
    containerWidth,
    containerHeight,

    findObjById,
    handleObjectDragMove,
    handleDragEnd,
    handleStageMouseDown,
    handleMouseEnterCanvas,
    handleMouseLeaveCanvas,
    handleMouseMoveCanvas,
    handleDragOver,
    handleDrop,
    handleMouseUpCanvas,
    isDragging
  };
}
